import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';
import { useHistory } from 'react-router-dom';

import { applyEvaluation, confirmEvaluationMatch, correctEvaluation, getEvaluation, getEvaluations, getLiveSummary, sendEvaluationToHuman } from '../../api/url_data_agent_evaluations';
import { getShopProductUrl } from '../../helpers/helpers';
import {
  EMPTY_LIVE_SUMMARY,
  SEND_BACK_REASON_OPTIONS,
  filterLiveMatchRows,
  getBulkResultMessage,
  getLiveActivityGroups,
  getLiveDateParams,
  getLiveMatchActions,
  getLiveDayStats,
  getLiveMatchProduct,
  getSendBackBody,
  isLiveMatchActionable,
  isNonProductDismissal,
  isSameApplyControl,
  readControlsFromSearch,
  runBulkAction,
  sortLiveMatchRows,
  writeControlsToSearch
} from '../../helpers/url_data_agent_live_matches_helpers';
import { getUrlDataLink } from '../../helpers/url_data_helpers';
import ConfirmPrompt from '../General/ConfirmPrompt';
import Tooltip from '../General/Tooltip';
import LiveMatchApplyModeControl from './LiveMatchApplyModeControl';
import LiveMatchDetailDrawer from './LiveMatchDetailDrawer';
import LiveMatchListControls from './LiveMatchListControls';
import LiveMatchProductPicker from './LiveMatchProductPicker';
import LiveMatchRow from './LiveMatchRow';
import LiveMatchRowSkeleton from './LiveMatchRowSkeleton';

import './SilentEvalLiveMatches.scss';

const PROPOSALS_LIMIT = 200;
const ACTIVITY_LIMIT = 100;
const REFRESH_MS = 30000;
const DEFAULT_FILTERS = { minConfidence: 0, domain: '', preownedOnly: false, mismatchOnly: false };
const SHORTCUTS_HINT = 'J/K move · X select · A approve · C different product · H send back · O open source · P open product · Enter details';
const INITIAL_CONTROLS = {
  queue: { ...DEFAULT_FILTERS, sort: 'confidence_desc' },
  activity: { date: '', state: 'all' }
};
const NO_ROWS = [];

const SilentEvalLiveMatches = ({ mode, active = true, search, onCounts, onTagClick }) => {
  const history = useHistory();
  const [summaryByDay, setSummaryByDay] = useState({});
  const [applyControl, setApplyControl] = useState(null);
  const [proposalsByKey, setProposalsByKey] = useState({});
  const [activityByKey, setActivityByKey] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [inFlight, setInFlight] = useState(0);
  // The URL seeds the list on screen; the other list keeps its own controls in memory until shown.
  const [controls, setControls] = useState(() => ({
    ...INITIAL_CONTROLS,
    [mode]: readControlsFromSearch(history.location.search, INITIAL_CONTROLS[mode])
  }));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [focusId, setFocusId] = useState(null);
  const [pickerRow, setPickerRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Non-null while a bulk run is in flight: { verb, done, total } drives the progress label.
  const [bulkState, setBulkState] = useState(null);
  const liveStateRef = useRef({});
  const detailRequestRef = useRef(null);
  const detailRequestSequenceRef = useRef(0);
  const applyControlRevisionRef = useRef(0);
  const beginDetailRequest = useCallback(id => {
    detailRequestRef.current = id;
    const sequence = ++detailRequestSequenceRef.current;
    return () => detailRequestRef.current === id && detailRequestSequenceRef.current === sequence;
  }, []);
  const actionInFlightRef = useRef(new Set());
  const bulkInFlightRef = useRef(false);

  // Display data
  const isQueue = mode === 'queue';
  const { date, state } = controls.activity;
  const summaryDate = date || moment().format('YYYY-MM-DD');
  const dayParams = getLiveDateParams(summaryDate);
  const proposalKey = search || '';
  const activityKey = `${date}|${state}|${search || ''}`;
  const proposalEntry = proposalsByKey[proposalKey];
  const activityEntry = activityByKey[activityKey];
  const proposals = proposalEntry?.rows || NO_ROWS;
  const nextProposalCursorId = proposalEntry?.nextCursorId || null;
  const activityRows = activityEntry?.rows || NO_ROWS;
  const nextCursorId = activityEntry?.nextCursorId || null;
  const summary = summaryByDay[summaryDate];
  const summaryError = !summary && loadErrors[`summary:${summaryDate}`];
  const visibleProposals = sortLiveMatchRows(filterLiveMatchRows(proposals, controls.queue), controls.queue.sort);
  const selectableProposals = visibleProposals.filter(isLiveMatchActionable);
  const visibleActivity = activityRows;
  const selectedProposals = proposals.filter(row => selectedIds.has(row.id));
  const selectedApprovableProposals = selectedProposals.filter(row => getLiveMatchActions(row).some(action => action.key === 'approve'));
  const allSelected = selectableProposals.length > 0 && selectableProposals.every(row => selectedIds.has(row.id));
  const queueTotal = proposalEntry ? proposalEntry.total : null;
  const actionableTotal = proposalEntry ? proposalEntry.total - proposals.filter(row => !isLiveMatchActionable(row)).length : null;
  const activityCount = activityEntry ? `${activityRows.length}${nextCursorId ? '+' : ''}` : null;
  const focusRows = isQueue ? visibleProposals : visibleActivity;
  // Focus is anchored by row id, not position: a background refresh that inserts or removes rows
  // can't silently move the keyboard onto a row the reviewer never looked at.
  const foundFocusIndex = focusRows.findIndex(row => row.id === focusId);
  const focusIndex = foundFocusIndex === -1 ? 0 : foundFocusIndex;
  const dayStats = getLiveDayStats(summary || EMPTY_LIVE_SUMMARY);
  // The drawer follows the live row (an approve moves it from queue to activity), and keeps the
  // row it opened with if a refresh drops it from both lists.
  const detailRow = detail && ([...proposals, ...activityRows].find(row => row.id === detail.id) || detail.row);

  // Data fetching — memoized because the mount/refresh effects key off them. Failures only surface
  // when the key has nothing to show yet; a refresh that fails keeps the stale rows quietly.
  const track = useCallback(async work => {
    setInFlight(n => n + 1);
    try {
      await work();
    } finally {
      setInFlight(n => n - 1);
    }
  }, []);

  const loadSummary = useCallback(
    () =>
      track(async () => {
        try {
          const { from, to } = getLiveDateParams(summaryDate);
          const resp = await getLiveSummary({ from, to });
          setSummaryByDay(cur => ({ ...cur, [summaryDate]: resp.summary }));
          setLoadErrors(cur => ({ ...cur, [`summary:${summaryDate}`]: null }));
        } catch (error) {
          setLoadErrors(cur => ({ ...cur, [`summary:${summaryDate}`]: "Could not load the day's numbers." }));
          console.warn(`Live summary unavailable: ${error?.message || error || 'unknown error'}`);
        }
      }),
    [summaryDate, track]
  );

  const loadProposals = useCallback(
    ({ cursorId = null, append = false, refreshCount = PROPOSALS_LIMIT } = {}) =>
      track(async () => {
        const key = search || '';
        const controlRevision = applyControlRevisionRef.current;
        if (append) setIsLoadingMore(true);
        try {
          const params = { applyLane: 'live', applyDecision: 'proposed', limit: PROPOSALS_LIMIT };
          if (search) params.search = search;
          let rows = [];
          let total;
          let nextCursorId = cursorId;
          do {
            if (nextCursorId) params.cursorId = nextCursorId;
            const resp = await getEvaluations(params);
            if (controlRevision !== applyControlRevisionRef.current) return;
            rows = _.uniqBy([...rows, ...(resp.evaluations || [])], 'id');
            total = resp.total ?? total;
            nextCursorId = resp.nextCursorId || null;
          } while (!append && refreshCount > PROPOSALS_LIMIT && nextCursorId && rows.length < refreshCount);
          setProposalsByKey(cur => ({
            ...cur,
            [key]: {
              rows: append ? _.uniqBy([...(cur[key]?.rows || []), ...rows], 'id') : rows,
              total: Number(total ?? cur[key]?.total ?? rows.length),
              nextCursorId
            }
          }));
          setLoadErrors(cur => ({ ...cur, [`queue:${key}`]: null }));
        } catch (error) {
          if (controlRevision !== applyControlRevisionRef.current) return;
          const message = `Failed to load links waiting for APM review: ${error?.message || error || 'unknown error'}`;
          if (append) window.ALERT.error(message);
          else setLoadErrors(cur => ({ ...cur, [`queue:${key}`]: message }));
        } finally {
          setIsLoadingMore(false);
        }
      }),
    [search, track]
  );

  const loadActivity = useCallback(
    ({ cursorId = null, append = false } = {}) =>
      track(async () => {
        const key = `${date}|${state}|${search || ''}`;
        if (append) setIsLoadingMore(true);
        try {
          const params = {
            applyLane: 'live',
            activity: 1,
            activityState: state === 'applied' ? 'matched' : state === 'passed' ? 'sent_to_human' : state,
            ...getLiveDateParams(date),
            excludeTotal: 1,
            limit: ACTIVITY_LIMIT
          };
          delete params.label;
          if (search) params.search = search;
          if (cursorId) params.cursorId = cursorId;
          const resp = await getEvaluations(params);
          const rows = resp.evaluations || [];
          setActivityByKey(cur => ({ ...cur, [key]: { rows: append ? _.uniqBy([...(cur[key]?.rows || []), ...rows], 'id') : rows, nextCursorId: resp.nextCursorId || null } }));
          setLoadErrors(cur => ({ ...cur, [`activity:${key}`]: null }));
        } catch (error) {
          const message = `Failed to load live-lane activity: ${error?.message || error || 'unknown error'}`;
          if (append) window.ALERT.error(message);
          else setLoadErrors(cur => ({ ...cur, [`activity:${key}`]: message }));
        } finally {
          setIsLoadingMore(false);
        }
      }),
    [date, state, search, track]
  );

  // Kept fresh before the fetch effects run so a refresh reads this render's paging depth.
  useEffect(() => {
    liveStateRef.current = {
      isBusy: busyIds.size > 0 || Boolean(bulkState),
      isLoadingMore,
      hasOverlay: Boolean(pickerRow || detail),
      activityCount: activityRows.length,
      proposalCount: proposals.length
    };
  });

  useEffect(() => () => {
    beginDetailRequest(null);
    applyControlRevisionRef.current += 1;
  }, [beginDetailRequest]);

  // Init + background sync
  useEffect(() => {
    setFocusId(null);
    loadProposals();
  }, [loadProposals]);

  useEffect(() => {
    if (!active || isQueue) return;
    loadSummary();
    if (liveStateRef.current.activityCount <= ACTIVITY_LIMIT) loadActivity();
  }, [active, isQueue, loadSummary, loadActivity]);

  useEffect(() => {
    if (onCounts) onCounts({ queue: actionableTotal, activity: activityCount });
  }, [onCounts, actionableTotal, activityCount]);

  const previousApplyControl = useRef(null);
  useEffect(() => {
    if (!applyControl) return;
    const previous = previousApplyControl.current;
    previousApplyControl.current = applyControl;
    if (!previous || isSameApplyControl(previous, applyControl)) return;
    applyControlRevisionRef.current += 1;
    loadProposals({ refreshCount: liveStateRef.current.proposalCount });
    const detailId = detailRequestRef.current;
    if (detailId) {
      const isCurrent = beginDetailRequest(detailId);
      getEvaluation(detailId).then(resp => {
        if (isCurrent()) setDetail(current => ({ ...current, evaluation: resp.evaluation }));
      }).catch(error => {
        if (isCurrent()) {
          window.ALERT.error(`Failed to refresh detail: ${error?.message || error || 'unknown error'}`);
        }
      });
    }
  }, [applyControl, loadProposals, beginDetailRequest]);

  // A teammate may approve selected rows from another tab, or APM may start checking one again; the
  // selection must not keep counting rows that no longer exist or that nobody can act on yet.
  useEffect(() => {
    setSelectedIds(current => {
      if (!current.size) return current;
      const alive = new Set(proposals.filter(isLiveMatchActionable).map(row => row.id).filter(id => current.has(id)));
      return alive.size === current.size ? current : alive;
    });
  }, [proposals]);

  useEffect(() => {
    // Silent background sync so teammates' actions and fresh worker activity appear without a
    // manual reload; skipped while an action or an overlay interaction is in flight.
    const tick = () => {
      const { isBusy, isLoadingMore: loadingMore, hasOverlay } = liveStateRef.current;
      if (!active || isBusy || loadingMore || hasOverlay) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (liveStateRef.current.proposalCount <= PROPOSALS_LIMIT) loadProposals();
      if (!isQueue) {
        loadSummary();
        if (liveStateRef.current.activityCount <= ACTIVITY_LIMIT) loadActivity();
      }
    };
    const interval = setInterval(tick, REFRESH_MS);
    return () => clearInterval(interval);
  }, [active, isQueue, loadSummary, loadProposals, loadActivity]);

  // Sort + filters, mirrored into the URL for whichever list is on screen
  const syncControlsToUrl = useCallback(
    next => history.replace({ pathname: history.location.pathname, search: writeControlsToSearch(history.location.search, next, INITIAL_CONTROLS[mode]) }),
    [history, mode]
  );
  const updateControls = patch => {
    const next = { ...controls[mode], ...patch };
    setControls(current => ({ ...current, [mode]: next }));
    syncControlsToUrl(next);
  };
  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  useEffect(() => {
    syncControlsToUrl(controlsRef.current[mode]);
  }, [mode, syncControlsToUrl]);

  // Actions
  const runAction = async (row, action) => {
    if (actionInFlightRef.current.has(row.id) || bulkInFlightRef.current) return;
    actionInFlightRef.current.add(row.id);
    setBusyIds(new Set(actionInFlightRef.current));
    const nextFocusId = row.id === focusRows[focusIndex]?.id ? getNextProposalFocusId(row.id) : undefined;
    let succeeded = false;
    try {
      const result = await action();
      if (result?.alreadyMatched) window.ALERT.success('Already linked to the suggested product.');
      if (result?.recoveryQueued) window.ALERT.success('APM is checking the existing catalog product automatically.');
      succeeded = true;
      if (detailRequestRef.current === row.id) closeDetail();
    } catch (error) {
      window.ALERT.error(`${error?.message || error || 'Action failed'}`);
    } finally {
      // Refetch on failure too: the server may have flipped the row's state mid-action (e.g. a
      // human matched the URL first), and a stale row would keep offering the same failing buttons.
      setActivityByKey({});
      await Promise.all([loadProposals({ refreshCount: proposals.length }), ...(!isQueue ? [loadSummary(), loadActivity()] : [])]);
      // The acted-on row moves over to Activity; the keyboard stays in the work queue.
      if (succeeded && nextFocusId !== undefined) setFocusId(nextFocusId);
      actionInFlightRef.current.delete(row.id);
      setBusyIds(new Set(actionInFlightRef.current));
    }
  };

  // The proposal that inherits focus once `id` leaves the queue: the one after it in the visible
  // (filtered, sorted) list, else the one before it, else null (top of the list). undefined when
  // `id` is not a visible proposal at all.
  const getNextProposalFocusId = id => {
    const index = visibleProposals.findIndex(row => row.id === id);
    if (index === -1) return undefined;
    return visibleProposals[index + 1]?.id ?? visibleProposals[index - 1]?.id ?? null;
  };

  // Selection + bulk actions. Bulk runs reuse the single-row endpoints with bounded concurrency,
  // so every row is still re-validated server-side; a mid-batch race fails that row alone and the
  // final refetch shows the true state.
  const toggleSelected = id =>
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Select all means the actionable rows that pass the current filters; rows hidden by a filter keep
  // their own selection state.
  const toggleSelectAll = () =>
    setSelectedIds(current => {
      const next = new Set(current);
      selectableProposals.forEach(row => (allSelected ? next.delete(row.id) : next.add(row.id)));
      return next;
    });

  const runBulk = async ({ rows, progressVerb, doneVerb, action }) => {
    if (!rows.length || bulkInFlightRef.current || actionInFlightRef.current.size) return;
    bulkInFlightRef.current = true;
    const focusedRowId = focusRows[focusIndex]?.id;
    setBulkState({ verb: progressVerb, done: 0, total: rows.length });
    try {
      const results = await runBulkAction(rows, action, {
        onProgress: (done, total) => setBulkState({ verb: progressVerb, done, total })
      });
      if (results.some(result => result.ok && result.row.id === detailRequestRef.current)) closeDetail();
      const result = getBulkResultMessage(results, doneVerb);
      if (result.ok) window.ALERT.success(result.message);
      else window.ALERT.error(result.message);
      setSelectedIds(new Set());
    } finally {
      setActivityByKey({});
      await Promise.all([loadProposals({ refreshCount: proposals.length }), ...(!isQueue ? [loadSummary(), loadActivity()] : [])]);
      if (rows.some(row => row.id === focusedRowId)) setFocusId(null);
      setBulkState(null);
      bulkInFlightRef.current = false;
    }
  };

  const confirmBulkApprove = () => {
    const rows = selectedApprovableProposals;
    if (!rows.length || bulkState) return;
    confirmAlert({
      title: `Approve ${rows.length} ${rows.length === 1 ? 'match' : 'matches'}?`,
      message: 'Each one is applied to the catalog, exactly like clicking Approve on the row.',
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        {
          label: `Approve ${rows.length}`,
          onClick: () => runBulk({ rows, progressVerb: 'Approving', doneVerb: 'Approved', action: row => applyEvaluation(row.id) })
        }
      ]
    });
  };

  const confirmBulkSendToHuman = () => {
    const rows = selectedProposals;
    if (!rows.length || bulkState) return;
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header={`Send ${rows.length} ${rows.length === 1 ? 'URL' : 'URLs'} to the catalog team?`}
          subheader='These links leave APM review and enter the catalog team’s queue.'
          selectOptions={SEND_BACK_REASON_OPTIONS}
          selectPlaceholder='Why does the catalog team need to review these links?'
          placeholder='Anything else? (optional, saved on every row)'
          rows={2}
          submitBtnDisplay={`Send ${rows.length} to catalog team`}
          onCancel={onClose}
          onSubmit={(note, reason) => {
            onClose();
            runBulk({
              rows,
              progressVerb: 'Sending',
              doneVerb: 'Sent back',
              action: row => sendEvaluationToHuman(row.id, getSendBackBody(reason, note))
            });
          }}
        />
      )
    });
  };

  const applyProposal = row => runAction(row, () => applyEvaluation(row.id));
  const sendToHuman = (row, reason, note) => runAction(row, () => sendEvaluationToHuman(row.id, getSendBackBody(reason, note)));
  const pickCorrection = (row, product) =>
    runAction(row, async () => {
      await correctEvaluation(row.id, { Product_id: product.id });
      setPickerRow(null);
    });

  const handleConfirmMatch = row => {
    confirmAlert({
      title: 'Confirm this match?',
      message: 'Confirm that you inspected the source page and the catalog product and found the match correct. This records your human review.',
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: 'Confirm match', onClick: () => runAction(row, () => confirmEvaluationMatch(row.id, { expectedRunId: row.runId, Product_id: row.applied_Product_id })) }
      ]
    });
  };

  const handleSendToHuman = row => {
    const isApplied = row.applyDecision === 'applied' && !row.revertedAt;
    const dismissal = isNonProductDismissal(row);
    const copy = !isApplied ? {
      header: 'Send to the catalog team?',
      subheader: 'This link leaves APM review and enters the catalog team’s queue.',
      reason: 'Why does the catalog team need to review this link?',
      submit: 'Send to catalog team'
    } : dismissal ? {
      header: 'Undo this dismissal?',
      subheader: 'The dismissal is removed if still owned by APM, and the URL returns to the catalog team.',
      reason: 'Why should this page be reviewed?',
      submit: 'Undo dismissal'
    } : {
      header: 'Undo this match?',
      subheader: 'The match is removed and the URL goes to the front of the human queue.',
      reason: 'Why is this match wrong?',
      submit: 'Undo match'
    };
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header={copy.header}
          subheader={copy.subheader}
          selectOptions={SEND_BACK_REASON_OPTIONS}
          selectPlaceholder={copy.reason}
          placeholder='Anything else the matcher should know? (optional)'
          rows={2}
          submitBtnDisplay={copy.submit}
          onCancel={onClose}
          onSubmit={(note, reason) => {
            onClose();
            sendToHuman(row, reason, note);
          }}
        />
      )
    });
  };

  const closeDetail = () => {
    beginDetailRequest(null);
    setDetail(null);
  };

  const toggleDetail = async row => {
    if (detail?.id === row.id) return closeDetail();
    const isCurrent = beginDetailRequest(row.id);
    setDetail({ id: row.id, row });
    try {
      const resp = await getEvaluation(row.id);
      // A slower earlier fetch (or one for a drawer the reviewer already closed) must not
      // overwrite the current selection.
      if (!isCurrent()) return;
      setDetail({ id: row.id, row, evaluation: resp.evaluation });
    } catch (error) {
      if (!isCurrent()) return;
      window.ALERT.error(`Failed to load detail: ${error?.message || error || 'unknown error'}`);
      closeDetail();
    }
  };

  const openInNewTab = url => url && window.open(url, '_blank', 'noopener');

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      if (!active) return;
      if (['TEXTAREA', 'INPUT', 'SELECT', 'BUTTON'].includes(e.target?.tagName) || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (pickerRow) return;
      // A confirm dialog (e.g. "Undo this match?") owns the keyboard while it is open.
      if (typeof document !== 'undefined' && document.querySelector('.react-confirm-alert')) return;
      if (e.key === 'Escape') {
        closeDetail();
        return;
      }
      const key = e.key.toLowerCase();
      if (key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusId(focusRows[Math.min(focusIndex + 1, Math.max(focusRows.length - 1, 0))]?.id ?? null);
        return;
      }
      if (key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusId(focusRows[Math.max(focusIndex - 1, 0)]?.id ?? null);
        return;
      }
      const row = focusRows[focusIndex];
      if (!row) return;
      // Mirror the row buttons' disabled state — a repeated keypress must not double-submit.
      if ((busyIds.has(row.id) || bulkState) && ['a', 'c', 'h', 'x'].includes(key)) return;
      const available = new Set(getLiveMatchActions(row).map(action => action.key));
      if (key === 'a' && available.has('approve')) {
        e.preventDefault();
        applyProposal(row);
      } else if (key === 'x' && isQueue && available.size > 0) {
        e.preventDefault();
        toggleSelected(row.id);
      } else if (key === 'c' && available.has('correct')) {
        e.preventDefault();
        setPickerRow(row);
      } else if (key === 'h' && (available.has('send_back') || available.has('undo'))) {
        e.preventDefault();
        handleSendToHuman(row);
      } else if (key === 'o') {
        e.preventDefault();
        openInNewTab(getUrlDataLink(row));
      } else if (key === 'p') {
        e.preventDefault();
        const product = getLiveMatchProduct(row);
        openInNewTab(product && getShopProductUrl(product.id));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        toggleDetail(row);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const renderRow = row => (
    <LiveMatchRow
      key={row.id}
      row={row}
      applyControl={applyControl}
      focused={row.id === focusRows[focusIndex]?.id}
      isBusy={busyIds.has(row.id) || (Boolean(bulkState) && selectedIds.has(row.id))}
      selectable={isQueue}
      selected={selectedIds.has(row.id)}
      onToggleSelect={() => toggleSelected(row.id)}
      onApply={() => applyProposal(row)}
      onCorrect={() => setPickerRow(row)}
      onSendToHuman={() => handleSendToHuman(row)}
      onToggleDetail={() => toggleDetail(row)}
      onTagClick={onTagClick}
    />
  );

  const lists = {
    queue: {
      rows: visibleProposals,
      isLoaded: Boolean(proposalEntry),
      error: loadErrors[`queue:${proposalKey}`],
      emptyText: proposals.length ? 'Nothing passes these filters.' : 'Nothing waiting to apply.'
    },
    activity: {
      rows: visibleActivity,
      isLoaded: Boolean(activityEntry),
      error: loadErrors[`activity:${activityKey}`],
      emptyText: 'No activity matches this date and outcome.'
    }
  };
  const list = lists[mode];

  return (
    <div className='silent-eval-live-matches'>
      <LiveMatchApplyModeControl onChange={setApplyControl} />
      {isQueue ? (
        <div className='lm-scope'>
          Waiting to apply · All dates. APM applies these or, after an hour, hands them to the catalog team; links it will not apply go there at once with the reason.
          {queueTotal > proposals.length && <span> Showing {visibleProposals.length} of {queueTotal} waiting.</span>}
        </div>
      ) : (
        <>
          <section className={cn('lm-metrics', { placeholder: !summary && !summaryError })} aria-label={`Links checked ${dayParams.label}`} aria-hidden={!summary && !summaryError}>
            {summaryError ? (
              <span role='status'>{summaryError}</span>
            ) : (
              <>
                <span className='lm-metrics-day'>Links checked {summaryDate === moment().format('YYYY-MM-DD') ? 'today' : dayParams.label}</span>
                {dayStats.map(stat => (
                  <span key={stat.key} className='lm-metric'>
                    {stat.label} <b>{stat.value}</b> {stat.detail && <small>{stat.detail}</small>}
                  </span>
                ))}
              </>
            )}
          </section>
          <div className='lm-scope'>Grouped by date checked, newest first. Each link shows its latest outcome.</div>
        </>
      )}

      <div className='se-toolbar'>
        <LiveMatchListControls mode={mode} controls={controls[mode]} onChange={updateControls} />
        <Tooltip message={SHORTCUTS_HINT} position='bottom' outerClassName='lm-help'>
          <button type='button' className='lm-help-btn' aria-label='Keyboard shortcuts'>
            ?
          </button>
        </Tooltip>
        {isQueue && (selectableProposals.length > 0 || selectedProposals.length > 0 || bulkState) && (
          <div className='lm-bulk-bar'>
            <label className='lm-select-all'>
              <input type='checkbox' checked={allSelected} onChange={toggleSelectAll} disabled={Boolean(bulkState) || !selectableProposals.length} />
              Select all
            </label>
            {bulkState ? (
              <span className='lm-bulk-progress'>
                {bulkState.verb} {bulkState.done}/{bulkState.total}…
              </span>
            ) : (
              selectedProposals.length > 0 && (
                <>
                  <span className='lm-bulk-count'>{selectedProposals.length} selected</span>
                  <button type='button' className='se-btn primary' disabled={busyIds.size > 0 || !selectedApprovableProposals.length} onClick={confirmBulkApprove}>
                    Approve {selectedApprovableProposals.length}
                  </button>
                  <button type='button' className='se-btn' disabled={busyIds.size > 0} onClick={confirmBulkSendToHuman}>
                    Send {selectedProposals.length} to catalog team
                  </button>
                  <button type='button' className='se-btn ghost' onClick={() => setSelectedIds(new Set())}>
                    Clear
                  </button>
                </>
              )
            )}
          </div>
        )}
        <span className={cn('se-refreshing', { on: inFlight > 0 })} aria-hidden='true' />
      </div>

      {!list.isLoaded ? (
        list.error ? (
          <div className='se-empty'>{list.error}</div>
        ) : (
          <div className='lm-list'>
            {_.range(3).map(i => (
              <LiveMatchRowSkeleton key={i} />
            ))}
          </div>
        )
      ) : !list.rows.length ? (
        <div className='se-empty'>{list.emptyText}</div>
      ) : (
        <div className={cn('lm-list', { selectable: isQueue })}>
          {isQueue ? list.rows.map(renderRow) : getLiveActivityGroups(list.rows).map(group => (
            <React.Fragment key={group.date}>
              <h3 className='lm-date-heading'>{group.label}</h3>
              {group.rows.map(renderRow)}
            </React.Fragment>
          ))}
          {!isQueue && nextCursorId && (
            <button type='button' className='se-btn lm-load-more' onClick={() => loadActivity({ cursorId: nextCursorId, append: true })} disabled={isLoadingMore}>
              {isLoadingMore ? 'Loading…' : 'Load older activity'}
            </button>
          )}
        </div>
      )}

      {isQueue && list.isLoaded && nextProposalCursorId && (
        <button type='button' className='se-btn lm-load-more' onClick={() => loadProposals({ cursorId: nextProposalCursorId, append: true })} disabled={isLoadingMore}>
          {isLoadingMore ? 'Loading…' : 'Load more waiting links'}
        </button>
      )}

      {pickerRow && (
        <LiveMatchProductPicker
          row={pickerRow}
          onPick={product => pickCorrection(pickerRow, product)}
          onClose={() => setPickerRow(null)}
          isSaving={busyIds.has(pickerRow.id)}
        />
      )}

      {detailRow && (
        <LiveMatchDetailDrawer
          row={detailRow}
          applyControl={applyControl}
          evaluation={detail.evaluation}
          isLoading={!detail.evaluation}
          isBusy={busyIds.has(detailRow.id)}
          onApply={() => applyProposal(detailRow)}
          onConfirm={!isQueue && detail.evaluation ? () => handleConfirmMatch(detail.evaluation) : undefined}
          onCorrect={() => setPickerRow(detail.evaluation || detailRow)}
          onPickCandidate={product => pickCorrection(detailRow, product)}
          onSendToHuman={() => handleSendToHuman(detailRow)}
          onClose={closeDetail}
        />
      )}
    </div>
  );
};

SilentEvalLiveMatches.propTypes = {
  mode: PropTypes.oneOf(['queue', 'activity']).isRequired,
  active: PropTypes.bool,
  search: PropTypes.string,
  onCounts: PropTypes.func,
  onTagClick: PropTypes.func.isRequired
};

export default SilentEvalLiveMatches;
